<template>
  <div class="home-cy-ai-tab content-main">
    <div class="content-main__centered">
      <!-- Section A: Large Prompt Input -->
      <div class="cy-ai-prompt-section">
        <div class="cy-ai-prompt-box">
          <div class="cy-ai-prompt-header">
            <icon-base icon="custom/ai" class="cy-ai-icon" />
            <span>On call and ready, how can I help?</span>
          </div>
          <v-textarea
            ref="promptInput"
            v-model="promptText"
            placeholder="Do anything with AI..."
            class="cy-ai-prompt-input no-label"
            hide-details
            rows="2"
            auto-grow
            @keydown.enter.prevent="handlePromptSubmit"
          />
          <div class="cy-ai-prompt-actions">
            <v-btn icon small class="mr-1">
              <v-icon small>mdi-plus</v-icon>
            </v-btn>
            <v-btn icon small>
              <v-icon small>mdi-tune-variant</v-icon>
            </v-btn>
            <v-spacer />
            <span class="cy-ai-prompt-mode">Auto</span>
            <v-btn
              fab
              x-small
              depressed
              :color="promptText.trim() ? 'primary' : 'grey lighten-2'"
              :disabled="!promptText.trim()"
              class="cy-ai-submit-btn"
              @click="handlePromptSubmit"
            >
              <v-icon small :color="promptText.trim() ? 'white' : 'grey'"
                >mdi-arrow-up</v-icon
              >
            </v-btn>
          </div>
        </div>
      </div>

      <!-- Section B: Quick Prompt Pills -->
      <div class="cy-ai-quick-prompts">
        <div class="cy-ai-quick-prompts-header">
          <span>Get started</span>
        </div>
        <div class="cy-ai-pills-row">
          <button
            v-for="prompt in quickPrompts"
            :key="prompt.label"
            class="cy-ai-pill"
            @click="handleQuickPromptClick(prompt)"
          >
            <v-icon small class="cy-ai-pill-icon">{{ prompt.icon }}</v-icon>
            <span>{{ prompt.label }}</span>
          </button>
        </div>
      </div>

      <!-- Section C: Conversations List -->
      <div class="cy-ai-conversations-section mt-8">
        <h4 class="section-header">Conversations</h4>

        <div
          v-if="conversationsLoading && conversations.length === 0"
          class="text-center py-4"
        >
          <v-progress-circular indeterminate size="24" color="primary" />
        </div>

        <div v-else-if="conversations.length === 0" class="cy-ai-empty-state">
          <p class="text-body-2 grey--text">
            Your conversations will appear here.
          </p>
        </div>

        <div v-else class="cy-ai-conversations-list">
          <div
            v-for="conversation in visibleConversations"
            :key="conversation.key"
            class="cy-ai-conversation-item"
            :class="{
              'cy-ai-conversation-item--active':
                isConversationActive(conversation),
            }"
            @click="handleConversationClick(conversation)"
          >
            <v-progress-circular
              v-if="isConversationActive(conversation)"
              indeterminate
              size="18"
              width="2"
              color="primary"
              class="mr-3"
            />
            <v-icon v-else small class="mr-3" color="primary"
              >mdi-message-text-outline</v-icon
            >
            <div class="cy-ai-conversation-content">
              <div class="cy-ai-conversation-name">
                {{ conversation.displayName || 'New conversation' }}
              </div>
              <div class="cy-ai-conversation-time">
                {{
                  isConversationActive(conversation)
                    ? 'Working...'
                    : formatRelativeTime(conversation.createdAt)
                }}
              </div>
            </div>
            <v-icon small color="grey">mdi-chevron-right</v-icon>
          </div>

          <v-btn
            v-if="conversations.length > 5 && !showAllConversations"
            text
            small
            color="primary"
            class="mt-2"
            @click="showAllConversations = true"
          >
            <v-icon small left>mdi-chevron-down</v-icon>
            Show {{ conversations.length - 5 }} more
          </v-btn>

          <v-btn
            v-if="showAllConversations && conversations.length > 5"
            text
            small
            color="primary"
            class="mt-2"
            @click="showAllConversations = false"
          >
            <v-icon small left>mdi-chevron-up</v-icon>
            Show less
          </v-btn>
        </div>
      </div>

      <!-- Section D: Custom Pages -->
      <div class="cy-ai-custom-pages-section mt-8">
        <h4 class="section-header">Custom Pages</h4>

        <div v-if="pagesLoading" class="text-center py-8">
          <v-progress-circular indeterminate color="primary" />
        </div>

        <div v-else-if="customPages.length === 0" class="cy-ai-empty-state">
          <v-card class="pa-6 text-center">
            <v-icon size="48" color="grey lighten-1" class="mb-4">
              mdi-file-document-multiple-outline
            </v-icon>
            <h5 class="text-h6 mb-2">No Custom Pages Yet</h5>
            <p class="text-body-2 grey--text">
              Use "Create custom page" above to build your first page.
            </p>
          </v-card>
        </div>

        <v-row v-else class="custom-pages-grid">
          <v-col v-for="page in customPages" :key="page.id" cols="12" sm="6">
            <v-card
              flat
              class="custom-page-card pa-4"
              @click="handleViewPage(page)"
            >
              <div class="d-flex align-center">
                <v-icon color="primary" class="mr-3">
                  mdi-file-document-outline
                </v-icon>
                <div class="flex-grow-1">
                  <div class="text-subtitle-1 font-weight-medium">
                    {{ page.name || 'Untitled Page' }}
                  </div>
                  <div class="text-caption grey--text">
                    {{ formatDate(page.updated_at || page.created_at) }}
                  </div>
                </div>
                <v-icon color="grey">mdi-chevron-right</v-icon>
              </div>
            </v-card>
          </v-col>
        </v-row>
      </div>

      <!-- Page View Modal -->
      <page-view-modal
        :record-id="selectedPageId"
        @close="selectedPageId = null"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions, mapState } from 'vuex';
import EventBus from '@/factories/eventBus';
import PageViewModal from '@/components/pages/PageViewModal.vue';

export default {
  name: 'HomeCyAiTab',
  components: { PageViewModal },
  data() {
    return {
      promptText: '',
      showAllConversations: false,
      selectedPageId: null,
      quickPrompts: [
        { icon: 'mdi-lightbulb-outline', label: 'What can Cy help with?' },
        {
          icon: 'mdi-file-document-outline',
          label: 'Create custom page',
          prefill: 'Help me build a page that ',
        },
        { icon: 'mdi-chart-line', label: 'Analyze my team metrics' },
        { icon: 'mdi-checkbox-marked-outline', label: 'Create a task tracker' },
      ],
    };
  },
  computed: {
    ...mapState('pages', { pagesLoading: 'loading' }),
    ...mapState('agents', ['loading', 'messages', 'backendAgentsLoading']),
    ...mapGetters('pages', ['recordsByLocation']),
    ...mapGetters('agents', [
      'chatInstances',
      'chatBackendAgents',
      'backendAgentsLoaded',
    ]),

    customPages() {
      return this.recordsByLocation('custom');
    },

    /**
     * Get conversations from backend agents.
     * Each agent has: id, name, type, uuid, created_at, updated_at
     * Note: created_at is a Unix timestamp in seconds from the API
     */
    conversations() {
      return this.chatBackendAgents.map((agent) => ({
        key: `chat:${agent.uuid}`,
        instanceName: agent.uuid,
        displayName: agent.name || 'New conversation',
        agentType: agent.type,
        // created_at is Unix timestamp in seconds, convert to milliseconds for JS Date
        createdAt: agent.created_at * 1000,
        backendId: agent.id,
      }));
    },

    visibleConversations() {
      if (this.showAllConversations) {
        return this.conversations;
      }
      return this.conversations.slice(0, 5);
    },

    conversationsLoading() {
      return this.backendAgentsLoading;
    },
  },
  methods: {
    ...mapActions('pages', ['fetchRecords']),
    ...mapActions('agents', ['fetchBackendAgents']),

    isConversationActive(conversation) {
      const key = conversation.key;

      // Check if loading (request in progress)
      if (this.loading[key]) {
        return true;
      }

      // Check if any message is streaming (only for connected agents)
      const msgs = this.messages[key] || [];
      if (msgs.length === 0) {
        // No messages in store means agent isn't connected, so not actively loading
        return false;
      }

      // Only check the last assistant message for streaming/pending status
      // Checking all messages can cause false positives if old messages weren't properly finalized
      const lastMsg = msgs[msgs.length - 1];
      return (
        lastMsg &&
        lastMsg.role === 'assistant' &&
        (lastMsg.status === 'streaming' || lastMsg.status === 'pending')
      );
    },

    generateUUID() {
      // Use crypto.randomUUID if available, fallback to manual generation
      if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return crypto.randomUUID();
      }
      // Fallback UUID v4 generation
      return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      });
    },

    handlePromptSubmit() {
      const prompt = this.promptText.trim();
      if (!prompt) return;

      const instanceName = this.generateUUID();

      EventBus.$emit('openChatAgent', {
        instanceName,
        search: prompt,
      });

      this.promptText = '';
    },

    handleQuickPromptClick(prompt) {
      // Populate the input box with either prefill text or the label
      this.promptText = prompt.prefill || prompt.label;
      this.$refs.promptInput?.$el.querySelector('textarea')?.focus();
    },

    handleConversationClick(conversation) {
      EventBus.$emit('openChatAgent', {
        instanceName: conversation.instanceName,
      });
    },

    handleViewPage(page) {
      this.selectedPageId = page.id;
    },

    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    },

    formatRelativeTime(timestamp) {
      if (!timestamp) return '';

      const now = Date.now();
      const diff = now - timestamp;

      const seconds = Math.floor(diff / 1000);
      const minutes = Math.floor(seconds / 60);
      const hours = Math.floor(minutes / 60);
      const days = Math.floor(hours / 24);

      if (seconds < 60) return 'Just now';
      if (minutes < 60)
        return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
      if (hours < 24) return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
      if (days < 7) return `${days} day${days !== 1 ? 's' : ''} ago`;

      return this.formatDate(new Date(timestamp).toISOString());
    },
  },
  mounted() {
    this.fetchRecords();
    this.fetchBackendAgents();
  },
};
</script>

<style lang="less" scoped>
.home-cy-ai-tab {
  .section-header {
    color: var(--v-appMain-base);
    margin-bottom: 16px;
    font-weight: 600;
  }

  // Prompt Section - Notion-style input
  .cy-ai-prompt-section {
    margin-bottom: 24px;
  }

  .cy-ai-prompt-box {
    background: #fff;
    border: 1px solid #e0e0e0;
    border-radius: 12px;
    padding: 16px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  }

  .cy-ai-prompt-header {
    display: flex;
    align-items: center;
    margin-bottom: 16px;
    font-size: 18px;
    font-weight: 600;
    color: #333;

    .cy-ai-icon {
      width: 32px;
      height: 32px;
      margin-right: 12px;
    }
  }

  .cy-ai-prompt-input {
    .v-input__slot {
      background: var(--v-lightGray-base) !important;
      border-radius: 8px;
      padding: 8px 12px !important;
    }

    textarea {
      font-size: 15px !important;
    }
  }

  .cy-ai-prompt-actions {
    display: flex;
    align-items: center;
    margin-top: 12px;
    padding-top: 8px;
  }

  .cy-ai-prompt-mode {
    font-size: 13px;
    color: #666;
    margin-right: 8px;
  }

  // Quick Prompts - Notion-style pills
  .cy-ai-quick-prompts {
    margin-bottom: 16px;
  }

  .cy-ai-quick-prompts-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 12px;
    font-weight: 500;
    color: #666;
    margin-bottom: 12px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .cy-ai-pills-row {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .cy-ai-pill {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 14px;
    background: #fff;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    font-size: 13px;
    color: #333;
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover {
      background: #f5f5f5;
      border-color: #ccc;
    }

    .cy-ai-pill-icon {
      color: #666;
    }
  }

  // Conversations List
  .cy-ai-conversations-section {
    .cy-ai-empty-state {
      padding: 16px 0;
    }
  }

  .cy-ai-conversations-list {
    background: #fff;
    border-radius: 8px;
    border: 1px solid #e0e0e0;
    overflow: hidden;
  }

  .cy-ai-conversation-item {
    display: flex;
    align-items: center;
    padding: 12px 16px;
    cursor: pointer;
    transition: background 0.15s ease;
    border-bottom: 1px solid #f0f0f0;

    &:last-child {
      border-bottom: none;
    }

    &:hover {
      background: #f9f9f9;
    }

    &--active {
      background: rgba(var(--v-primary-base-rgb, 104, 205, 255), 0.08);

      .cy-ai-conversation-time {
        color: var(--v-primary-base);
        font-weight: 500;
      }
    }
  }

  .cy-ai-conversation-content {
    flex: 1;
    min-width: 0;
  }

  .cy-ai-conversation-name {
    font-weight: 500;
    font-size: 14px;
    color: #333;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .cy-ai-conversation-time {
    font-size: 12px;
    color: #999;
    margin-top: 2px;
  }

  // Custom Pages Grid
  .custom-pages-grid {
    margin-top: 8px;
  }

  .custom-page-card {
    cursor: pointer;
    border: 1px solid #e0e0e0;
    transition: background 0.15s ease;

    &:hover {
      background: #f9f9f9;
    }
  }

  .cy-ai-empty-state {
    max-width: 400px;
  }
}
</style>
